import React, { useEffect } from 'react';
import './styles/scss/app.scss';
import { CssBaseline } from '@mui/material';
import { Route, Routes, BrowserRouter, Navigate, Outlet } from 'react-router-dom';
import LInvestor from '../apps/investor/layouts/LInvestor';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import themeDarkConfig from './themes/themeDarkConfig';
import HAppRoutes from './features/routes/helpers/HAppRoutes';
import { useAppTheme } from './features/theme/providers/AppThemeProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import AppError404 from './features/error/404/AppError404';
import AuthGate from '../apps/auth/features/gate/AuthGate';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import useAuthOkta from './hooks/useAuthOkta';
import { AuthProvider, useAuthContext } from '../apps/auth/contexts/AuthContext';
import { useAppLang } from './features/lang/providers/AppLangProvider';
import { QueryParamProvider } from 'use-query-params';

import i18next from 'i18next';
import AppNavigationProvider from './features/navigation/providers/AppNavigationProvider';
import themeDefaultConfig from './themes/themeDefaultConfig';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from './utils/queryClient';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import FAppScrollToTop from './features/scroll-to-top/FAppScrollToTop';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { Helmet } from 'react-helmet';
import FAppErrorProbe from './features/error/probe/FAppErrorProbe';
import AuthRoutes from '../apps/auth/AuthRoutes';
import AppErrorBoundary from './features/error/boundary/AppErrorBoundary';
import moment from 'moment';
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import 'moment/locale/fr';
import { useOidc, useOidcIdToken, useOidcAccessToken } from '@axa-fr/react-oidc';

const App = () => {
	const appTheme = useAppTheme();

	HAppRoutes.checkRoutes();

	const oktaAuth = useAuthOkta();

	const { language } = useAppLang();
	moment.locale(i18next.language);
	useEffect(() => {
		if (language !== i18next.language) {
			i18next.changeLanguage(language);
			moment.locale(language);
		}
	}, [language]);

	const restoreOriginalUri = async (_oktaAuth: unknown, originalUri: string) => {
		window.location.replace(toRelativeUrl(originalUri || '/', window.location.origin));
	};

	const { pushInstruction, enableLinkTracking } = useMatomo();

	useEffect(() => {
		const onCookieConsentChange = (event: CustomEvent) => {
			if (event?.detail?.statistic) {
				pushInstruction('rememberCookieConsentGiven');
				pushInstruction('rememberConsentGiven');
				enableLinkTracking();
			}
		};

		window.addEventListener('CookieConsentChange', onCookieConsentChange as EventListener, false);

		return () => {
			window.removeEventListener('CookieConsentChange', onCookieConsentChange as EventListener);
		};
	}, [enableLinkTracking, pushInstruction]);

	const oidc = useOidc();
	const { accessToken } = useOidcAccessToken();
	const { idTokenPayload } = useOidcIdToken();
	const { setAuthContext, setAuthAccessToken, setAuthUserProfile } = useAuthContext();
	useEffect(() => {
		if (oidc.isAuthenticated) {
			setAuthContext('employee');
			setAuthAccessToken(accessToken);
			setAuthUserProfile(idTokenPayload);
		}
	}, [accessToken, oidc.isAuthenticated, idTokenPayload, setAuthAccessToken, setAuthContext, setAuthUserProfile]);
	return (
		<>
			<Helmet>
				<title>SALT by Sienna IM</title>
			</Helmet>
			<QueryClientProvider client={queryClient}>
				<AuthProvider>
					<LocalizationProvider dateAdapter={AdapterMoment}>
						<ThemeProvider
							theme={
								appTheme.theme === 'dark'
									? createTheme(themeDefaultConfig, themeDarkConfig)
									: createTheme(themeDefaultConfig)
							}
						>
							<CssBaseline enableColorScheme />

							<Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
								<BrowserRouter>
									<QueryParamProvider adapter={ReactRouter6Adapter}>
										<AppErrorBoundary>
											<FAppErrorProbe>
												<AppNavigationProvider>
													<FAppScrollToTop>
														<Routes>
															{/* Investor-protected routes share a single <LInvestor> via <Outlet/>.
															    Layout stays mounted across navigations so menu/drawer/queries
															    don't tear down on every page switch. */}
															<Route
																element={
																	<LInvestor>
																		<Outlet />
																	</LInvestor>
																}
															>
																{HAppRoutes.getRoutes()
																	.filter((r) => r.layout === 'investor')
																	.map((routeConfig) => (
																		<Route
																			key={routeConfig.tag}
																			path={routeConfig.path}
																			element={
																				<AuthGate
																					authRequired={routeConfig.auth}
																					currentUri={
																						location.pathname +
																						location.search
																					}
																				>
																					{routeConfig.component}
																				</AuthGate>
																			}
																		/>
																	))}
															</Route>
															{HAppRoutes.getRoutes()
																.filter((r) => r.layout !== 'investor')
																.map((routeConfig) => (
																	<Route
																		key={routeConfig.tag}
																		path={routeConfig.path}
																		element={
																			<AuthGate
																				authRequired={routeConfig.auth}
																				currentUri={
																					location.pathname + location.search
																				}
																			>
																				{routeConfig.component}
																			</AuthGate>
																		}
																	/>
																))}
															<Route
																path="/"
																element={
																	<Navigate
																		to={HAppRoutes.getPath(AuthRoutes.login.tag)}
																		replace
																	/>
																}
															/>
															<Route path="*" element={<AppError404 />} />
														</Routes>
													</FAppScrollToTop>
												</AppNavigationProvider>
											</FAppErrorProbe>
										</AppErrorBoundary>
									</QueryParamProvider>
								</BrowserRouter>
							</Security>
						</ThemeProvider>
					</LocalizationProvider>
				</AuthProvider>
				<ReactQueryDevtools />
			</QueryClientProvider>
		</>
	);
};

export default App;
